import { motion } from "framer-motion";
import { Download } from "lucide-react";
import SectionHeader from "./SectionHeader";

const BROCHURE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/brochure`;

const GUIDELINES = [
  { title: "Eligibility", body: "Tradella is open to students of Grades 10 to 12 only. Please verify individual event eligibility criteria before registering." },
  { title: "Participation", body: "Each student may participate in only one event." },
  { title: "Team Registration & Payment", body: "Each team must register through a single form submission, and the full payment must be made in one installment. Multiple entries for the same team will not be accepted. Allotment will be confirmed only after full payment is received." },
  { title: "Registration Fee", body: "A fee of ₹500 per team per event is applicable. All payments must be made through the designated QR code payment method." },
  { title: "Reporting Time", body: "All participants must report to Harvest International School, Kodathi by 8:00 AM." },
  { title: "Transport", body: "Participants are responsible for their own transportation to and from the venue." },
  { title: "Refreshments", body: "Food and beverages will be available at designated stalls within the school campus for participants and accompanying teachers. Refreshments may be purchased as required." },
  { title: "Event Conclusion", body: "The event is expected to conclude at approximately 6:00 PM." },
  { title: "Dress Code", body: "All participants must be dressed in their official school uniform or formals throughout the duration of the event." },
  { title: "Code of Conduct", body: "Any form of malpractice, misconduct, or violation of event rules will result in immediate disqualification." },
  { title: "Use of Technology", body: "Use of gadgets and devices is NOT permitted unless specified in the event brief." },
  { title: "Destruction of Property", body: "Participants must handle venue property and equipment responsibly. Any damage may result in disqualification and/or liability for damages." },
  { title: "ID Verification", body: "Students must carry a valid school ID card along with any event-specific materials required for participation." },
  { title: "Substance Use Policy", body: "The use of any prohibited substances is strictly forbidden. Any participant found in possession or use will face immediate disqualification." },
  { title: "Belongings", body: "Participants are responsible for their own belongings. The school shall not be held liable for any loss or damage." },
  { title: "Decision of Judges", body: "The decision of the event heads and judges shall be final and binding. No appeals will be entertained." },
  { title: "Result Declaration", body: "Event results will be announced during the closing ceremony." },
];

export default function Guidelines() {
  return (
    <section
      id="guidelines"
      data-testid="guidelines-section"
      className="relative py-20 sm:py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-12">
        <SectionHeader
          eyebrow="Guidelines & Brochure"
          title={<><span className="text-marble">The </span><span className="italic text-gold-gradient">Codex</span></>}
          subtitle="Read the rules of engagement. Download the brochure for the full details of each arena."
          align="center"
          testId="guidelines-header"
        />

        {/* Brochure CTA */}
        <div className="mt-10 flex justify-center">
          <a
            href={BROCHURE_URL}
            target="_blank"
            rel="noopener noreferrer"
            download
            data-testid="brochure-download-btn"
            className="btn-sheen inline-flex items-center gap-3 px-8 py-4 bg-[#B8873B] text-black font-semibold uppercase tracking-[0.3em] text-[11px] sm:text-xs hover:bg-[#E6C88A] transition-colors duration-300"
          >
            <Download size={16} />
            Download Brochure
          </a>
        </div>

        {/* Guidelines grid */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#B8873B]/20">
          {GUIDELINES.map((g, i) => (
            <motion.div
              key={g.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, delay: (i % 8) * 0.04 }}
              className="bg-[#0A0A0A] p-6 md:p-7 flex gap-4 hover:bg-[#0f0f0f] transition-colors"
              data-testid={`guideline-${i}`}
            >
              <div className="shrink-0 w-10 h-10 border border-[#B8873B]/50 grid place-items-center text-[#B8873B] font-display text-lg">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div>
                <div className="font-display text-marble text-xl md:text-2xl leading-tight">{g.title}</div>
                <p className="mt-2 text-white/60 text-sm leading-relaxed">{g.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
